import { Box, Card, Title, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  AtsGetRecruitmentAiSettingsDocument,
  UpdateRecruitmentAiSettingsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useAtsContext } from 'modules/ats/lib/atsContext'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const FactorialAIPage = () => {
  const { company } = useCurrentSession()
  const { data, loading } = useQuery(AtsGetRecruitmentAiSettingsDocument, {
    variables: {
      companyID: company.id,
    },
  })
  const { canManageAtsSettings } = useAtsContext()
  const [updateRecruitmentAISettings] = useMutation(UpdateRecruitmentAiSettingsDocument, {
    extractorFn: (aiData) => aiData.atsMutations.updateAtsCompany,
  })

  const companySettings = data?.ats.companiesConnection.edges[0]?.node ?? false

  if (!companySettings) {
    return null
  }

  const aiMatchingEnabled = companySettings.aiMatchingEnabled
  const candidateSummaryEnabled = companySettings.candidateSummaryEnabled

  const onToggleAIMatching = async (newAIMatchingValue: boolean | undefined) => {
    const result = handleResult(
      await updateRecruitmentAISettings({
        variables: {
          id: companySettings.id,
          aiMatchingEnabled: newAIMatchingValue,
        },
      }),
      () => undefined
    )
    if (result.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
    }
  }

  const onToggleCandidateSummary = async (newCandidateSummaryValue: boolean | undefined) => {
    const result = handleResult(
      await updateRecruitmentAISettings({
        variables: {
          id: companySettings.id,
          candidateSummaryEnabled: newCandidateSummaryValue,
        },
      }),
      () => undefined
    )
    if (result.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
    }
  }

  return (
    <ResponsivePage.Body
      loading={loading}
      error={null}
      authorized={canManageAtsSettings}
      showHeader={false}
    >
      <Box gap="s48">
        <Box gap="s24">
          <Title
            illustration="rocket"
            variant="500"
            title={i18n.t('ats.settings.factorial_ai.title')}
            description={i18n.t('ats.settings.factorial_ai.description')}
          />

          <Box gap="s16">
            <Card>
              <Card.Title
                size="small"
                action={
                  <TogglerCheckbox
                    value={aiMatchingEnabled || false}
                    onChange={(event) => onToggleAIMatching(event)}
                  />
                }
              >
                {i18n.t('ats.settings.factorial_ai.ai_matching.title')}
              </Card.Title>
              <Card.Description>
                {i18n.t('ats.settings.factorial_ai.ai_matching.description')}
              </Card.Description>
            </Card>
            <Card>
              <Card.Title
                size="small"
                action={
                  <TogglerCheckbox
                    value={candidateSummaryEnabled || false}
                    onChange={(event) => onToggleCandidateSummary(event)}
                  />
                }
              >
                {i18n.t('ats.settings.factorial_ai.candidate_summary.title')}
              </Card.Title>
              <Card.Description>
                {i18n.t('ats.settings.factorial_ai.candidate_summary.description')}
              </Card.Description>
            </Card>
          </Box>
        </Box>
      </Box>
    </ResponsivePage.Body>
  )
}

export default FactorialAIPage
